import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Copy, Facebook, MessageCircle, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { getInstrument, getCourse } from "@/data/curriculum";
import { api } from "@/lib/api";
import { CertificateArt } from "@/pages/Certificate";

export default function PublicCertificate() {
  const { certId } = useParams();
  const [cert, setCert] = useState(undefined);
  useEffect(() => { api.get(`/certificates/public/${certId}`).then(({ data }) => setCert(data)).catch(() => setCert(null)); }, [certId]);
  const [inst, cid] = (cert?.course_key || ":").split(":");
  const instrument = getInstrument(inst);
  const course = getCourse(inst, cid);
  const url = window.location.href;
  const title = instrument && course ? (course.mini ? course.title : `${instrument.label} — ${course.title}`) : "";
  const text = cert ? `${cert.name} earned the ${title} certificate on The JT Hub!` : "";
  const open = (u) => window.open(u, "_blank", "noopener");
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 lg:py-14" data-testid="public-certificate-page">
        {cert === undefined && <p className="text-primary">Loading…</p>}
        {cert === null && <p className="font-serif text-2xl" data-testid="public-certificate-missing">Certificate not found.</p>}
        {cert && (
          <>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">Verified JT Hub certificate</p>
            <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl" data-testid="public-certificate-name">{cert.name}</h1>
            <div className="mt-8 overflow-hidden rounded-2xl border border-border shadow-xl" data-testid="public-certificate-card">
              <CertificateArt name={cert.name} courseTitle={title} tagline={course?.tagline}
                dateStr={new Date(cert.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`)} variant="outline" className="rounded-full border-border" data-testid="public-share-whatsapp"><MessageCircle size={15} className="mr-2" /> WhatsApp</Button>
              <Button onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)} variant="outline" className="rounded-full border-border" data-testid="public-share-facebook"><Facebook size={15} className="mr-2" /> Facebook</Button>
              <Button onClick={() => open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`)} variant="outline" className="rounded-full border-border" data-testid="public-share-twitter"><Twitter size={15} className="mr-2" /> X / Twitter</Button>
              <Button onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} variant="outline" className="rounded-full border-border" data-testid="public-share-copy"><Copy size={15} className="mr-2" /> Copy link</Button>
            </div>
            <div className="mt-10 rounded-2xl border border-primary/40 bg-primary/10 p-6">
              <p className="font-serif text-xl">Learn to read music with Jason Thompson</p>
              <p className="mt-1 text-sm text-muted-foreground">Courses for singers, drummers, guitarists and pianists — start from absolute zero.</p>
              <Link to="/courses" className="mt-4 inline-block" data-testid="public-certificate-cta"><Button className="rounded-full bg-primary text-primary-foreground">Explore the courses</Button></Link>
              <p className="mt-2 text-sm font-semibold text-muted-foreground" data-testid="public-certificate-menu-hint">or click on the Menu tab for more information</p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
